/**
 * System Monitoring API Client
 * GPU infrastructure, workers, and resource monitoring
 */

import { getAPIClient } from './client';

export interface GPUInstanceStatus {
  name: string;
  host: string;
  port: number;
  status: 'healthy' | 'unhealthy' | 'unknown';
  gpu_count: number;
  gpu_model: string;
  speed_tokens_per_sec: number | null;
  models_loaded: string[] | null;
  response_time_ms: number | null;
  last_checked: string;
  /** Reason when unhealthy (e.g. "Connection refused", "ReadTimeout") */
  unhealthy_reason?: string | null;
  /** Error classification: timeout, connection_refused, http_error */
  error_type?: string | null;
}

export interface LiteLLMStatus {
  status: 'healthy' | 'degraded' | 'down';
  url: string;
  healthy_backends: number;
  unhealthy_backends: number;
  healthy_endpoints: string[];
  unhealthy_endpoints: string[];
  response_time_ms: number | null;
  last_checked: string;
  /** When down: timeout = may be transient; connection_refused = proxy not running */
  error_type?: string | null;
  error_message?: string | null;
}

export interface WorkerStatus {
  worker_name: string;
  status: 'active' | 'offline';
  queues: string[];
  active_tasks: number;
  processed_tasks: number;
  failed_tasks: number;
  concurrency: number;
  pool_type: string;
}

export interface SystemResourcesStatus {
  cpu_percent: number;
  memory_percent: number;
  memory_used_mb: number;
  memory_total_mb: number;
  python_processes: number;
  celery_workers: number;
  api_servers: number;
}

export interface LoadBalancerBackend {
  name: string;
  host: string;
  port: number;
  gpu_count: number;
  gpu_model: string;
  speed_tokens_per_sec: number;
  num_parallel: number;
  active_requests: number;
  utilization_percent: number;
}

export interface LoadBalancerStatus {
  total_backends: number;
  total_capacity: number;
  backends: LoadBalancerBackend[];
}

export interface SystemMonitoringResponse {
  timestamp: string;
  /** Keys: production, development, and any extra from TRACEOS_GPU_EXTRA (e.g. 10 GPUs) */
  gpu_infrastructure: Record<string, GPUInstanceStatus[]>;
  litellm_proxy: LiteLLMStatus;
  load_balancer: LoadBalancerStatus;
  celery_workers: WorkerStatus[];
  local_resources: SystemResourcesStatus;
  recommendations: string[];
  alerts: string[];
}

export interface GPUSummary {
  total_gpus: number;
  total_instances: number;
  servers: number;
  infrastructure: {
    production: {
      host: string;
      instances: Array<{
        port: number;
        name: string;
        gpu_count: number;
        gpu_model: string;
        speed: number | null;
      }>;
    };
    development: {
      host: string;
      instances: Array<{
        port: number;
        name: string;
        gpu_count: number;
        gpu_model: string;
        speed: number | null;
      }>;
    };
  };
}

/**
 * Get comprehensive system monitoring data
 * Includes GPU infrastructure, LiteLLM proxy, workers, and local resources
 */
export async function getSystemMonitoring(): Promise<SystemMonitoringResponse> {
  const client = getAPIClient();
  return client.get<SystemMonitoringResponse>('/api/system/monitor');
}

/**
 * Get quick GPU infrastructure summary
 */
export async function getGPUSummary(): Promise<GPUSummary> {
  const client = getAPIClient();
  return client.get<GPUSummary>('/api/system/gpu/summary');
}

/**
 * Restart a specific Celery worker
 */
export async function restartWorker(workerName: string): Promise<{ message: string }> {
  const client = getAPIClient();
  return client.post<{ message: string }>(`/api/system/workers/${workerName}/restart`);
}
